import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { API_URL } from './adminUtils';
import {
  Globe,
  Settings,
  FileText,
  Save,
  RefreshCw,
  Loader2,
  Eye,
} from 'lucide-react';

const SiteSettingsManagement = () => {
  const [settings, setSettings] = useState({
    site_title: '',
    hero_title: '',
    hero_subtitle: '',
    footer_text: '',
    constitution_amendment_mode: false
  });
  const [smtpSettings, setSmtpSettings] = useState({
    smtp_enabled: false,
    smtp_host: 'smtp.gmail.com',
    smtp_port: 587,
    smtp_email: '',
    smtp_password: '',
    notification_email: ''
  });
  const [emailTemplate, setEmailTemplate] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingSmtp, setSavingSmtp] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [activeTab, setActiveTab] = useState('site');
  const [showTemplatePreview, setShowTemplatePreview] = useState(false);

  const fetchSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/settings`);
      setSettings(response.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSmtpSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/settings/smtp`);
      setSmtpSettings(response.data);
    } catch (error) {
    }
  }, []);

  const fetchEmailTemplate = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/settings/email-templates`);
      setEmailTemplate(response.data.event_submission_template || '');
    } catch (error) {
    }
  }, []);

  useEffect(() => {
    fetchSettings();
    fetchSmtpSettings();
    fetchEmailTemplate();
  }, [fetchSettings, fetchSmtpSettings, fetchEmailTemplate]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/settings`, settings);
      toast.success('Site settings updated successfully');
    } catch (error) {
      toast.error('Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSmtp = async () => {
    setSavingSmtp(true);
    try {
      await axios.put(`${API_URL}/settings/smtp`, smtpSettings);
      toast.success('SMTP settings updated successfully');
    } catch (error) {
      toast.error('Failed to update SMTP settings');
    } finally {
      setSavingSmtp(false);
    }
  };

  const handleTestEmail = async () => {
    setTestingEmail(true);
    try {
      await axios.post(`${API_URL}/settings/smtp/test`, {});
      toast.success('Test email sent successfully! Check your inbox.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test email');
    } finally {
      setTestingEmail(false);
    }
  };

  const handleSaveTemplate = async () => {
    setSavingTemplate(true);
    try {
      await axios.put(`${API_URL}/settings/email-templates`, 
        { event_submission_template: emailTemplate }
      );
      toast.success('Email template saved successfully');
    } catch (error) {
      toast.error('Failed to save email template');
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleResetTemplate = async () => {
    try {      const response = await axios.post(`${API_URL}/settings/email-templates/reset`, {});
      setEmailTemplate(response.data.templates.event_submission_template || '');
      toast.success('Email template reset to default');
    } catch (error) {
      toast.error('Failed to reset template');
    }
  };

  const getPreviewHtml = () => {
    // Replace template variables with sample data for preview
    return emailTemplate
      .replace(/\{\{event_title\}\}/g, 'Annual Cultural Festival')
      .replace(/\{\{submitter_name\}\}/g, 'John Doe')
      .replace(/\{\{organization_name\}\}/g, 'Ngoketunjia Cultural Association')
      .replace(/\{\{event_date\}\}/g, 'March 15, 2025')
      .replace(/\{\{submitted_at\}\}/g, new Date().toLocaleString());
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="font-serif text-2xl font-bold">Site Settings</h1>
        <p className="text-muted-foreground">Customize landing page content, branding, and email notifications</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="site" className="flex items-center gap-2">
            <Globe className="w-4 h-4" />
            Site Content
          </TabsTrigger>
          <TabsTrigger value="email" className="flex items-center gap-2">
            <Settings className="w-4 h-4" />
            SMTP Config
          </TabsTrigger>
          <TabsTrigger value="templates" className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Email Templates
          </TabsTrigger>
        </TabsList>

        {/* Site Content Tab */}
        <TabsContent value="site" className="space-y-6 mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="font-serif flex items-center gap-2">
                <Globe className="w-5 h-5 text-cameroon-green" />
                Landing Page Content
              </CardTitle>
              <CardDescription>
                Edit the main content displayed on the home page
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label>Site Title</Label>
                <Input
                  value={settings.site_title || ''}
                  onChange={(e) => setSettings({ ...settings, site_title: e.target.value })}
                  placeholder="CCA Montreal"
                  data-testid="site-title-input"
                />
                <p className="text-xs text-muted-foreground">Displayed in the browser tab and navbar</p>
              </div>

              <div className="space-y-2">
                <Label>Hero Title</Label>
                <Input
                  value={settings.hero_title || ''}
                  onChange={(e) => setSettings({ ...settings, hero_title: e.target.value })}
                  placeholder="Council of Cultural Associations"
                  data-testid="hero-title-input"
                />
                <p className="text-xs text-muted-foreground">Main headline on the landing page</p>
              </div>

              <div className="space-y-2">
                <Label>Hero Subtitle</Label>
                <Textarea
                  value={settings.hero_subtitle || ''}
                  onChange={(e) => setSettings({ ...settings, hero_subtitle: e.target.value })}
                  placeholder="Uniting Cameroonian cultural associations..."
                  rows={3}
                  data-testid="hero-subtitle-input"
                />
                <p className="text-xs text-muted-foreground">Descriptive text below the main headline</p>
              </div>

              <div className="space-y-2">
                <Label>Footer Text</Label>
                <Input
                  value={settings.footer_text || ''}
                  onChange={(e) => setSettings({ ...settings, footer_text: e.target.value })}
                  placeholder="© 2025 Council of Cultural Associations..."
                  data-testid="footer-text-input"
                />
                <p className="text-xs text-muted-foreground">Copyright and legal text in the footer</p>
              </div>
              
              {/* Constitution Amendment Mode */}
              <div className="border-t pt-4 mt-4">
                <div className="flex items-center justify-between p-4 rounded-lg bg-amber-50 border border-amber-200">
                  <div className="flex-1">
                    <h4 className="font-semibold text-amber-900 flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      Constitution Amendment Mode
                    </h4>
                    <p className="text-xs text-amber-700 mt-1">
                      When enabled, the homepage will display the constitution signing/compiled section. 
                      When disabled, featured events carousel will be shown instead.
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant={settings.constitution_amendment_mode ? "default" : "outline"}
                    onClick={() => setSettings(prev => ({ ...prev, constitution_amendment_mode: !prev.constitution_amendment_mode }))}
                    className={settings.constitution_amendment_mode ? "bg-amber-600 hover:bg-amber-700 text-white ml-4" : "ml-4"}
                    data-testid="amendment-mode-toggle"
                  >
                    {settings.constitution_amendment_mode ? 'Enabled' : 'Disabled'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Preview Card */}
          <Card className="border-dashed">
            <CardHeader>
              <CardTitle className="text-sm flex items-center gap-2">
                <Eye className="w-4 h-4" />
                Preview
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="p-6 bg-gradient-to-br from-cameroon-green/10 to-cameroon-yellow/10 rounded-lg text-center">
                <h2 className="font-serif text-2xl font-bold mb-2">
                  {settings.hero_title || 'Council of Cultural Associations'}
                </h2>
                <p className="text-muted-foreground max-w-lg mx-auto">
                  {settings.hero_subtitle || 'Uniting 13 Cameroonian cultural associations in Montreal.'}
                </p>
              </div>
              <p className="text-center text-sm text-muted-foreground mt-4">
                {settings.footer_text || '© 2025 Council of Cultural Associations. All rights reserved.'}
              </p>
            </CardContent>
          </Card>

          <div className="flex gap-3">
            <Button 
              onClick={handleSave} 
              disabled={saving}
              className="bg-cameroon-green hover:bg-cameroon-green/90"
              data-testid="save-settings-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Settings'}
            </Button>
            <Button variant="outline" onClick={fetchSettings}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Reset
            </Button>
          </div>
        </TabsContent>

        {/* Email Notifications Tab */}
        <TabsContent value="email" className="space-y-6 mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="font-serif flex items-center gap-2">
                <Settings className="w-5 h-5 text-cameroon-yellow" />
                Gmail SMTP Configuration
              </CardTitle>
              <CardDescription>
                Configure email notifications for signatures and event submissions
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Enable Toggle */}
              <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                <div>
                  <Label className="text-base">Enable Email Notifications</Label>
                  <p className="text-sm text-muted-foreground">Send emails for signatures and event updates</p>
                </div>
                <Button
                  variant={smtpSettings.smtp_enabled ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSmtpSettings({ ...smtpSettings, smtp_enabled: !smtpSettings.smtp_enabled })}
                  className={smtpSettings.smtp_enabled ? "bg-cameroon-green hover:bg-cameroon-green/90" : ""}
                  data-testid="smtp-enabled-toggle"
                >
                  {smtpSettings.smtp_enabled ? 'Enabled' : 'Disabled'}
                </Button>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>SMTP Host</Label>
                  <Input
                    value={smtpSettings.smtp_host || ''}
                    onChange={(e) => setSmtpSettings({ ...smtpSettings, smtp_host: e.target.value })}
                    placeholder="smtp.gmail.com"
                    data-testid="smtp-host-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>SMTP Port</Label>
                  <Input
                    type="number"
                    value={smtpSettings.smtp_port || 587}
                    onChange={(e) => setSmtpSettings({ ...smtpSettings, smtp_port: parseInt(e.target.value) })}
                    placeholder="587"
                    data-testid="smtp-port-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Gmail Address</Label>
                <Input
                  type="email"
                  value={smtpSettings.smtp_email || ''}
                  onChange={(e) => setSmtpSettings({ ...smtpSettings, smtp_email: e.target.value })}
                  placeholder="your-email@gmail.com"
                  data-testid="smtp-email-input"
                />
                <p className="text-xs text-muted-foreground">The Gmail account to send emails from</p>
              </div>

              <div className="space-y-2">
                <Label>App Password</Label>
                <Input
                  type="password"
                  value={smtpSettings.smtp_password || ''}
                  onChange={(e) => setSmtpSettings({ ...smtpSettings, smtp_password: e.target.value })}
                  placeholder="••••••••••••••••"
                  data-testid="smtp-password-input"
                />
                <p className="text-xs text-muted-foreground">
                  Use a <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener noreferrer" className="text-primary underline">Gmail App Password</a>, not your regular password
                </p>
              </div>

              <div className="space-y-2">
                <Label>Notification Email</Label>
                <Input
                  type="email"
                  value={smtpSettings.notification_email || ''}
                  onChange={(e) => setSmtpSettings({ ...smtpSettings, notification_email: e.target.value })}
                  placeholder="admin@cca-montreal.ca"
                  data-testid="notification-email-input"
                />
                <p className="text-xs text-muted-foreground">Where admin notifications will be sent (signatures, new events)</p>
              </div>
            </CardContent>
          </Card>

          {/* Notification Info */}
          <Card className="bg-cameroon-green/5 border-cameroon-green/20">
            <CardContent className="p-4">
              <h4 className="font-medium mb-2">Email Notifications Will Be Sent For:</h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>✉️ When a president signs the constitution</li>
                <li>✉️ When a new event is submitted for approval</li>
                <li>✉️ When an event is approved or rejected (sent to association)</li>
              </ul>
            </CardContent>
          </Card>

          <div className="flex gap-3">
            <Button 
              onClick={handleSaveSmtp} 
              disabled={savingSmtp}
              className="bg-cameroon-green hover:bg-cameroon-green/90"
              data-testid="save-smtp-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              {savingSmtp ? 'Saving...' : 'Save SMTP Settings'}
            </Button>
            <Button 
              variant="outline" 
              onClick={handleTestEmail}
              disabled={testingEmail || !smtpSettings.smtp_enabled}
              data-testid="test-email-btn"
            >
              {testingEmail ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Settings className="w-4 h-4 mr-2" />
              )}
              {testingEmail ? 'Sending...' : 'Send Test Email'}
            </Button>
          </div>
        </TabsContent>

        {/* Email Templates Tab */}
        <TabsContent value="templates" className="space-y-6 mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="font-serif flex items-center gap-2">
                <FileText className="w-5 h-5 text-cameroon-red" />
                New Event Submission Email Template
              </CardTitle>
              <CardDescription>
                Customize the HTML email sent to admins when a new event is submitted for approval.
                Use variables like <code className="bg-muted px-1 rounded">{'{{event_title}}'}</code>, <code className="bg-muted px-1 rounded">{'{{submitter_name}}'}</code>, <code className="bg-muted px-1 rounded">{'{{organization_name}}'}</code>, <code className="bg-muted px-1 rounded">{'{{event_date}}'}</code>, <code className="bg-muted px-1 rounded">{'{{submitted_at}}'}</code>
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>HTML Template</Label>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowTemplatePreview(!showTemplatePreview)}
                    data-testid="toggle-preview-btn"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    {showTemplatePreview ? 'Hide Preview' : 'Show Preview'}
                  </Button>
                </div>
                <Textarea
                  value={emailTemplate}
                  onChange={(e) => setEmailTemplate(e.target.value)}
                  placeholder="<div>Your HTML email template...</div>"
                  rows={12}
                  className="font-mono text-sm"
                  data-testid="email-template-textarea"
                />
              </div>

              {/* Template Variables Reference */}
              <div className="p-4 bg-muted/50 rounded-lg">
                <h4 className="font-medium mb-2 text-sm">Available Variables:</h4>
                <div className="grid grid-cols-2 gap-2 text-sm text-muted-foreground">
                  <code className="bg-background px-2 py-1 rounded">{'{{event_title}}'}</code>
                  <span>Name of the event</span>
                  <code className="bg-background px-2 py-1 rounded">{'{{submitter_name}}'}</code>
                  <span>Name of person submitting</span>
                  <code className="bg-background px-2 py-1 rounded">{'{{organization_name}}'}</code>
                  <span>Association or &quot;Other&quot;</span>
                  <code className="bg-background px-2 py-1 rounded">{'{{event_date}}'}</code>
                  <span>Event date</span>
                  <code className="bg-background px-2 py-1 rounded">{'{{submitted_at}}'}</code>
                  <span>Submission timestamp</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Preview Card */}
          {showTemplatePreview && (
            <Card className="border-dashed">
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <Eye className="w-4 h-4" />
                  Email Preview (with sample data)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div 
                  className="border rounded-lg p-4 bg-white"
                  dangerouslySetInnerHTML={{ __html: getPreviewHtml() }}
                  data-testid="email-preview"
                />
              </CardContent>
            </Card>
          )}

          <div className="flex gap-3">
            <Button 
              onClick={handleSaveTemplate} 
              disabled={savingTemplate}
              className="bg-cameroon-green hover:bg-cameroon-green/90"
              data-testid="save-template-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              {savingTemplate ? 'Saving...' : 'Save Template'}
            </Button>
            <Button variant="outline" onClick={handleResetTemplate} data-testid="reset-template-btn">
              <RefreshCw className="w-4 h-4 mr-2" />
              Reset to Default
            </Button>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SiteSettingsManagement;
